import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Download, RefreshCw, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Signature capture & display for a cart's requirements sheet.
 * - If already signed → shows signature preview + download PDF button + option to re-sign
 * - If not signed → shows canvas for drawing signature + name field + submit button
 */
export default function SignatureSection({ serial }) {
    const [state, setState] = useState({ loading: true, signed: false, signature: null });
    const [editing, setEditing] = useState(false);
    const [signerName, setSignerName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const hasStrokeRef = useRef(false);

    async function refresh() {
        setState((s) => ({ ...s, loading: true }));
        try {
            const { data } = await api.get(`/account/carts/${encodeURIComponent(serial)}/signature`);
            setState({ loading: false, signed: !!data?.signed, signature: data?.signed ? data : null });
        } catch {
            setState({ loading: false, signed: false, signature: null });
        }
    }
    useEffect(() => { refresh(); }, [serial]);

    // Canvas drawing handlers
    function initCanvas() {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const rect = canvas.getBoundingClientRect();
        canvas.width = Math.floor(rect.width * dpr);
        canvas.height = Math.floor(rect.height * dpr);
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.strokeStyle = "#0B2545";
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        hasStrokeRef.current = false;
    }
    useEffect(() => {
        // Init canvas whenever the signing form is visible
        const showForm = !state.signed || editing;
        if (showForm) {
            setTimeout(initCanvas, 40);
        }
    }, [editing, state.signed]);

    function getPoint(e) {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        if (e.touches && e.touches[0]) {
            return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    function startDraw(e) {
        e.preventDefault();
        drawingRef.current = true;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const p = getPoint(e);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
    }
    function moveDraw(e) {
        if (!drawingRef.current) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const p = getPoint(e);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        hasStrokeRef.current = true;
    }
    function endDraw() {
        drawingRef.current = false;
    }

    function clearCanvas() {
        initCanvas();
    }

    async function submit() {
        if (!hasStrokeRef.current) return toast.error("Desenhe a sua assinatura antes de submeter.");
        if (!signerName.trim() || signerName.trim().length < 2) return toast.error("Indique o seu nome (mín. 2 caracteres).");
        setSubmitting(true);
        try {
            const dataUrl = canvasRef.current.toDataURL("image/png");
            await api.post(`/account/carts/${encodeURIComponent(serial)}/sign`, {
                signature_data_url: dataUrl,
                signer_name: signerName.trim(),
            });
            toast.success("Folha de requisitos assinada.");
            setEditing(false);
            setSignerName("");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao guardar assinatura.");
        } finally {
            setSubmitting(false);
        }
    }

    async function downloadPdf() {
        try {
            const res = await api.get(`/account/carts/${encodeURIComponent(serial)}/requirements.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `NOVIFOOD-Requisitos-${serial}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao descarregar.");
        }
    }

    if (state.loading) {
        return (
            <section className="rounded-2xl border border-border bg-white p-6" data-testid="signature-loading">
                <div className="text-sm text-muted-foreground">A verificar assinatura…</div>
            </section>
        );
    }

    // Already signed view
    if (state.signed && !editing) {
        const sig = state.signature;
        return (
            <section className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-6" data-testid="signature-signed">
                <div className="flex items-start gap-3">
                    <ShieldCheck className="h-6 w-6 text-emerald-700 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <h3 className="font-heading text-lg font-black text-primary">Folha de requisitos assinada</h3>
                        <p className="mt-1 text-sm text-emerald-900/80">
                            Assinada por <strong>{sig.signer_name}</strong> em {new Date(sig.signed_at).toLocaleString("pt-PT")}.
                        </p>
                    </div>
                </div>
                {sig.signature_data_url && (
                    <div className="mt-4 rounded-lg border border-border bg-white p-3 flex items-center justify-center">
                        <img src={sig.signature_data_url} alt="assinatura" className="max-h-24 object-contain" data-testid="signature-preview" />
                    </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={downloadPdf}
                        data-testid="btn-download-signed-pdf"
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-bold hover:-translate-y-0.5 transition">
                        <Download className="h-4 w-4" /> Descarregar PDF assinado
                    </button>
                    <button onClick={() => setEditing(true)}
                        data-testid="btn-resign"
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-bold text-primary hover:bg-muted">
                        <RefreshCw className="h-4 w-4" /> Voltar a assinar
                    </button>
                </div>
            </section>
        );
    }

    // Signing view
    return (
        <section className="rounded-2xl border border-border bg-white p-6" data-testid="signature-form">
            <div className="flex items-start gap-3">
                <FileSignature className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h3 className="font-heading text-lg font-black text-primary">
                        {state.signed ? "Voltar a assinar a folha de requisitos" : "Assinar folha de requisitos"}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Ao assinar, aceita a lista de peças entregues com o seu carrinho. O documento fica disponível para download em PDF.
                    </p>
                </div>
            </div>

            <label className="mt-4 block">
                <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">O seu nome *</span>
                <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Nome completo"
                    data-testid="signer-name"
                    className="mt-1 w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white" />
            </label>

            <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Assine no campo abaixo</span>
                    <button type="button" onClick={clearCanvas}
                        data-testid="signature-clear"
                        className="text-xs text-primary hover:underline">Limpar</button>
                </div>
                <div className="rounded-lg border-2 border-dashed border-border bg-white select-none touch-none" style={{ height: 180 }}>
                    <canvas ref={canvasRef}
                        data-testid="signature-canvas"
                        className="w-full h-full rounded-lg cursor-crosshair"
                        onMouseDown={startDraw}
                        onMouseMove={moveDraw}
                        onMouseUp={endDraw}
                        onMouseLeave={endDraw}
                        onTouchStart={startDraw}
                        onTouchMove={moveDraw}
                        onTouchEnd={endDraw}
                    />
                </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={submit} disabled={submitting}
                    data-testid="signature-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold hover:-translate-y-0.5 transition disabled:opacity-50">
                    {submitting ? "A guardar…" : "Guardar assinatura"}
                </button>
                {state.signed && (
                    <button onClick={() => setEditing(false)}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-bold text-primary hover:bg-muted">
                        Cancelar
                    </button>
                )}
            </div>
        </section>
    );
}
